import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { LANGUAGES, CONTACT_INFO } from "../mock";

const Navbar = () => {
  const { lang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "pranic", label: t.nav.pranic },
    { id: "tachyon", label: t.nav.tachyon },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-cream/90 backdrop-blur-md shadow-[0_2px_30px_rgba(76,62,99,0.08)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          data-testid="home-scroll-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex flex-col items-start leading-none group"
        >
          <span className="font-serif text-2xl text-brand-plumDark tracking-wide">
            Francesca De Salvia
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brand-gold mt-0.5">
            Pranic Healing · Tenerife
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              data-testid={`navigation-${l.id}-button`}
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-[15px] text-brand-ink/80 hover:text-brand-plum transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-brand-gold after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}

          {/* Language selector */}
          <div className="relative">
            <button
              data-testid="language-selector-button"
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="flex items-center gap-1 text-sm text-brand-ink/80 hover:text-brand-plum transition-colors border border-brand-gold/40 rounded-full px-3 py-1"
            >
              {LANGUAGES.find((x) => x.code === lang)?.label}
              <ChevronDown size={14} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-lg border border-brand-sand overflow-hidden">
                {LANGUAGES.map((l) => (
                  <a
                    key={l.code}
                    href={l.href}
                    onClick={() => setLangOpen(false)}
                    data-testid={`language-link-${l.code}`}
                    className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-brand-sand transition-colors ${
                      lang === l.code ? "text-brand-plum font-medium" : "text-brand-ink/80"
                    }`}
                  >
                    {l.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            data-testid="desktop-whatsapp-booking-link"
            href={CONTACT_INFO.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-plum hover:bg-brand-plumDark text-brand-cream text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            {t.nav.cta}
          </a>
        </nav>

        {/* Mobile toggles */}
        <div className="lg:hidden flex items-center gap-3">
          <div className="flex items-center gap-1 border border-brand-gold/40 rounded-full px-2 py-1">
            {LANGUAGES.map((l) => (
              <a
                key={l.code}
                href={l.href}
                data-testid={`mobile-language-link-${l.code}`}
                className={`text-xs px-1.5 py-0.5 rounded-full transition-colors ${
                  lang === l.code ? "text-brand-plum font-semibold" : "text-brand-ink/60"
                }`}
              >
                {l.label}
              </a>
            ))}
          </div>
          <button data-testid="mobile-menu-toggle-button" onClick={() => setOpen((v) => !v)} className="text-brand-plumDark">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-brand-cream/98 backdrop-blur-md border-t border-brand-sand mt-3 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              data-testid={`mobile-navigation-${l.id}-button`}
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-left text-lg text-brand-ink/85 hover:text-brand-plum transition-colors"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="mobile-whatsapp-booking-link"
            href={CONTACT_INFO.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="bg-brand-plum text-brand-cream text-center px-5 py-3 rounded-full mt-2"
          >
            {t.nav.cta}
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
